import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChildInfo, getChildren } from '../../../api/childrenApi';
import {
  CollectionLiabilityEntry,
  LiabilityStatus,
  getCollectionLiabilities,
} from '../../../api/collectionLiabilitiesApi';
import { ChevronRightIcon } from '../../../components/icons';
import { DataTable, DataTableCell, DataTableHeader, DataTableHeaderCell, DataTableRow } from '../../../components/ui/DataTable';
import { formatLongDate } from '../../../utils/calendar';
import { getStatusColors, STATUS_LABEL_KEYS } from './liabilityStatus';

type StatusTab = 'ALL' | 'PAID' | 'PENDING' | 'EXPIRED' | 'CANCELLED';

const PAGE_SIZE = 20;

const STATUS_TABS: { value: StatusTab; labelKey: string }[] = [
  { value: 'ALL', labelKey: 'collections.tabAll' },
  { value: 'PAID', labelKey: 'collections.tabPaid' },
  { value: 'PENDING', labelKey: 'collections.tabUnpaid' },
  { value: 'EXPIRED', labelKey: 'collections.statusExpired' },
  { value: 'CANCELLED', labelKey: 'collections.statusCancelled' },
];

function formatAmount(amount: number): string {
  return `${amount.toFixed(2).replace('.', ',')} zł`;
}

export default function CollectionsListView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [children, setChildren] = useState<ChildInfo[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<StatusTab>('ALL');
  const [liabilities, setLiabilities] = useState<CollectionLiabilityEntry[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getChildren().then((res) => setChildren(res.rows));
  }, []);

  useEffect(() => {
    setLoading(true);
    const statuses: LiabilityStatus[] | undefined =
      statusTab === 'ALL' ? undefined : [statusTab];
    const childIds = selectedChildId ? [selectedChildId] : undefined;

    getCollectionLiabilities({ childIds, statuses, page, size: PAGE_SIZE })
      .then((res) => {
        setLiabilities((prev) => (page === 0 ? res.rows : [...prev, ...res.rows]));
        setTotalElements(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .finally(() => setLoading(false));
  }, [selectedChildId, statusTab, page]);

  function handleSelectChild(childId: string | null) {
    setSelectedChildId(childId);
    setPage(0);
  }

  function handleSelectStatusTab(tab: StatusTab) {
    setStatusTab(tab);
    setPage(0);
  }

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      {children.length > 1 && (
        <ChildSelector children={children} selectedChildId={selectedChildId} onSelect={handleSelectChild} />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h4">{t('sidebar.collections')}</Typography>
          <Typography variant="h4" color="text.secondary">
            {totalElements}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={() => navigate('/parent/execute-payment')}
          sx={(theme) => ({
            backgroundColor: theme.app.primary,
            color: theme.app.primaryForeground,
            '&:hover': { backgroundColor: theme.app.primaryHover },
          })}
        >
          {t('collections.payOverdue')}
        </Button>
      </Box>

      <StatusTabs value={statusTab} onChange={handleSelectStatusTab} />

      {loading && page === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <DataTable>
            <TableHeader />
            {liabilities.map((liability) => (
              <LiabilityRow
                key={liability.id}
                liability={liability}
                onClick={() => navigate(`/parent/collections/${liability.id}`)}
                onPay={() => navigate(`/parent/execute-payment?liabilityId=${liability.id}`)}
              />
            ))}
          </DataTable>
        </Box>
      )}

      {page + 1 < totalPages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => setPage((p) => p + 1)}>{t('collections.loadMore')}</Button>
        </Box>
      )}
    </Box>
  );
}

function ChildSelector({
  children,
  selectedChildId,
  onSelect,
}: {
  children: ChildInfo[];
  selectedChildId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
      <ChildPill label={t('collections.allChildren')} selected={selectedChildId === null} onClick={() => onSelect(null)} />
      {children.map((child) => (
        <ChildPill
          key={child.id}
          label={`${child.firstName} ${child.lastName}`}
          selected={selectedChildId === child.id}
          onClick={() => onSelect(child.id)}
        />
      ))}
    </Box>
  );
}

function ChildPill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        px: 2,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${theme.app.border}`,
        backgroundColor: selected ? theme.app.secondary : theme.app.card,
        cursor: 'pointer',
        userSelect: 'none',
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

function StatusTabs({ value, onChange }: { value: StatusTab; onChange: (tab: StatusTab) => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {STATUS_TABS.map((tab) => {
        const selected = tab.value === value;
        return (
          <Button
            key={tab.value}
            onClick={() => onChange(tab.value)}
            variant={selected ? 'contained' : 'outlined'}
            disableRipple
            sx={(theme) => ({
              minHeight: 36,
              px: 2,
              py: 1,
              borderRadius: '9999px',
              backgroundColor: selected ? theme.app.accentSurface : 'transparent',
              color: selected ? theme.app.accent : theme.app.foreground,
              borderColor: selected ? theme.app.accentSurface : theme.app.borderStrong,
              '&:hover': {
                backgroundColor: selected ? theme.app.accentSurface : theme.app.secondary,
              },
            })}
          >
            {t(tab.labelKey)}
          </Button>
        );
      })}
    </Box>
  );
}

export function TableHeader() {
  const { t } = useTranslation();
  return (
    <DataTableHeader>
      <DataTableHeaderCell flex={2}>{t('collections.columnName')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('collections.columnChild')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('collections.columnIssueDate')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('collections.columnAmount')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('collections.columnDueDate')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('collections.columnStatus')}</DataTableHeaderCell>
      <DataTableCell flex="0 0 100px">{null}</DataTableCell>
    </DataTableHeader>
  );
}

function StatusChip({ status }: { status: LiabilityStatus }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={(theme) => {
        const { bg, color } = getStatusColors(theme, status);
        return { display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 99, backgroundColor: bg, color };
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {t(STATUS_LABEL_KEYS[status])}
      </Typography>
    </Box>
  );
}

export function LiabilityRow({
  liability,
  onClick,
  onPay,
}: {
  liability: CollectionLiabilityEntry;
  onClick: () => void;
  onPay: () => void;
}) {
  const { t } = useTranslation();
  const child = liability.payer.child;
  const childLabel = child ? `${child.firstName} ${child.lastName[0] ?? ''}.` : '-';
  const isPaid = liability.status === 'PAID';

  return (
    <DataTableRow onClick={onClick}>
      <DataTableCell flex={2}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {liability.collection.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {liability.collection.organization?.name}
        </Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography variant="body2">{childLabel}</Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography variant="body2">{formatLongDate(liability.collection.createdAt)}</Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatAmount(liability.collection.amount)}
        </Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography
          variant="body2"
          sx={(theme) => ({ color: liability.status === 'PENDING' ? theme.app.destructive : 'inherit' })}
        >
          {formatLongDate(liability.collection.deadline)}
        </Typography>
      </DataTableCell>
      <DataTableCell>
        <StatusChip status={liability.status} />
      </DataTableCell>
      <DataTableCell flex="0 0 100px" align="right">
        {isPaid ? (
          <Box
            sx={(theme) => ({
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: theme.app.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 'auto',
              color: theme.app.mutedForeground,
            })}
          >
            <ChevronRightIcon size={16} />
          </Box>
        ) : (
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={(e) => {
              e.stopPropagation();
              onPay();
            }}
            sx={(theme) => ({ backgroundColor: theme.app.primary, color: theme.app.primaryForeground })}
          >
            {t('collections.pay')}
          </Button>
        )}
      </DataTableCell>
    </DataTableRow>
  );
}
